import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { rowsToCsv } from '@resource-kit/controllers';
import type { TranslateFn } from '@resource-kit/model';
import { formatFieldTextForExport, type ExportContext } from '../registry/export-text';

/**
 * Column-capability helpers for ListView's toolbar (#1288): default
 * visibility derivation, the toggleable-column set, human labels, and CSV
 * projection of the current page.
 *
 * These operate on raw `ColumnDef`s (not a live TanStack table instance) so
 * they are pure and unit-testable, and so ListView can drive both the
 * show/hide menu and the export from the same column array it already holds.
 * They are generic over BOTH of `ColumnDef`'s parameters because it is
 * invariant in both: a non-generic `ColumnDef<unknown>` signature would reject
 * every concrete `ColumnDef<Row>` caller, and pinning the second parameter to
 * `unknown` rejects a `ColumnDef<Row, Value>` caller for exactly the same
 * reason — `ListView` and `useColumnVisibility` are both generic in it, so they
 * hold one (#2169 PR-3). `TValue` DEFAULTS to `unknown` so the many callers
 * that write `ColumnDef<Row>` keep the signature they had.
 *
 * None of these functions READ a cell value at its declared type — they work in
 * ids, labels and text — so widening the parameter costs nothing here and buys
 * a caller that does not have to launder its columns through a cast.
 */

// Mechanism columns are framework-owned, not data: the row-actions column
// (`__row_actions__`) and any future internal column follow the `__name__`
// convention. They never appear in the toggle menu nor the CSV. (The selection
// checkbox is rendered by DataTable outside the column model, so it needs no
// exclusion here.)
const MECHANISM_ID = /^__.*__$/;

/** Resolve a column's stable id the same way TanStack derives it. */
export function columnId<T, TValue = unknown>(column: ColumnDef<T, TValue>): string | undefined {
  const accessor = (column as { accessorKey?: unknown }).accessorKey;
  if (typeof accessor === 'string') return accessor;
  return typeof column.id === 'string' ? column.id : undefined;
}

/** True for framework-owned mechanism columns (e.g. row actions). */
export function isMechanismColumn<T, TValue = unknown>(column: ColumnDef<T, TValue>): boolean {
  const id = columnId(column);
  return id != null && MECHANISM_ID.test(id);
}

/**
 * Schema-default visibility: a map that hides only the columns whose
 * `meta.hidden` is set. Columns absent from the map are visible by default —
 * matching TanStack's `columnVisibility` semantics. This is the seed state for
 * the session-local visibility the operator then toggles.
 */
export function computeDefaultColumnVisibility<T, TValue = unknown>(
  columns: ColumnDef<T, TValue>[],
): VisibilityState {
  const visibility: VisibilityState = {};
  for (const column of columns) {
    const id = columnId(column);
    if (!id) continue;
    if (column.meta?.hidden === true) visibility[id] = false;
  }
  return visibility;
}

/** Ids of columns the operator may show/hide (excludes mechanism columns). */
export function getToggleableColumnIds<T, TValue = unknown>(
  columns: ColumnDef<T, TValue>[],
): string[] {
  const ids: string[] = [];
  for (const column of columns) {
    if (isMechanismColumn(column)) continue;
    const id = columnId(column);
    if (id) ids.push(id);
  }
  return ids;
}

/**
 * Human label for a column: the schema `meta.label` (a raw i18n key) when
 * present, else a string `header`, else the column id. Falls back gracefully
 * for hand-built columns that carry no schema meta.
 *
 * Takes a bare translator rather than the `ExportContext` its two callers hold:
 * a label is a key lookup and has nothing to format, and a parameter it would
 * never read is a special case rather than a symmetry.
 */
export function columnLabel<T, TValue = unknown>(
  column: ColumnDef<T, TValue>,
  translate: TranslateFn,
): string {
  const metaLabel = column.meta?.label;
  if (typeof metaLabel === 'string' && metaLabel) {
    return translate(metaLabel, { defaultValue: metaLabel });
  }
  if (typeof column.header === 'string') return column.header;
  return columnId(column) ?? '';
}

/** A column is exported when it is neither a mechanism column nor hidden. */
function isExportable<T, TValue = unknown>(
  column: ColumnDef<T, TValue>,
  visibility: VisibilityState,
): boolean {
  if (isMechanismColumn(column)) return false;
  const id = columnId(column);
  if (!id) return false;
  return visibility[id] !== false;
}

/**
 * Text value for a single cell in the export. Schema columns (those carrying a
 * back-referenced `meta.field`) reuse the cell's exact formatting via
 * `formatFieldTextForExport`. Hand-built columns with a string `accessorKey`
 * degrade to the raw value string. Id-only columns that supply an `accessorFn`
 * (the TanStack derivation for computed values — e.g. an `actor_label ??
 * actor_id` fallback) reuse that function so the export mirrors the cell's
 * value instead of exporting empty. Anything else exports empty.
 */
function cellText<T, TValue = unknown>(
  column: ColumnDef<T, TValue>,
  row: T,
  ctx: ExportContext,
): string {
  const field = column.meta?.field;
  const accessor = (column as { accessorKey?: unknown }).accessorKey;
  const accessorFn = (column as { accessorFn?: unknown }).accessorFn;
  const record = row as Record<string, unknown> | null | undefined;

  if (field) {
    const raw = record?.[field.key as string];
    return formatFieldTextForExport(field, raw, row, ctx);
  }

  if (typeof accessor === 'string') {
    const raw = record?.[accessor];
    return raw === null || raw === undefined ? '' : String(raw);
  }

  if (typeof accessorFn === 'function') {
    const raw = (accessorFn as (row: T, index: number) => unknown)(row, 0);
    return raw === null || raw === undefined ? '' : String(raw);
  }

  return '';
}

/**
 * Project the current page's rows to an RFC 4180 CSV string: header row of the
 * visible, non-mechanism column labels followed by one row per record, each
 * cell formatted to match its on-screen rendering. Injection/quoting escaping
 * is handled by `rowsToCsv`.
 */
/**
 * Project the visible columns of `data` into a header row + one row per
 * record — the shared basis for BOTH the CSV export and the built-in print
 * sheet.
 *
 * Shared deliberately: a printout whose columns disagree with the
 * spreadsheet is a bug nobody notices until the paper is in a hand, and the
 * only way to make that unreachable is to give the two one projection rather
 * than two that must be kept aligned.
 */
export function tableToRows<T, TValue = unknown>(
  columns: ColumnDef<T, TValue>[],
  data: readonly T[],
  visibility: VisibilityState,
  ctx: ExportContext,
): string[][] {
  const cols = columns.filter((c) => isExportable(c, visibility));
  const header = cols.map((c) => columnLabel(c, ctx.t));
  const body = data.map((row) => cols.map((c) => cellText(c, row, ctx)));
  return [header, ...body];
}

export function tableToCsv<T, TValue = unknown>(
  columns: ColumnDef<T, TValue>[],
  data: T[],
  visibility: VisibilityState,
  ctx: ExportContext,
): string {
  return rowsToCsv(tableToRows(columns, data, visibility, ctx));
}
